/**
 * The browser's Supabase client.
 *
 * SCOPE — read this before adding a call.
 * This client is used for AUTHENTICATION ONLY: sign in, sign out, set a
 * password, refresh a session. All application data goes through the backend
 * API (see lib/api.js), per Decision #38 — one server-side data path means one
 * place for payload shape, audit writes and future rate limiting.
 *
 * The anon key below is public by design. It is in the shipped bundle and
 * anyone can read it. That is safe because row-level security, not key
 * secrecy, is the boundary: the key alone grants nothing until a real user
 * JWT accompanies it. The SERVICE ROLE key is a different animal entirely and
 * must never appear in this directory — vite.config.js fails the build if it
 * finds one.
 */
import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Fail at boot with a message that names the fix. A missing variable
  // otherwise surfaces as an inscrutable network error on the login form.
  throw new Error(
    'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must both be set.\n' +
      'Locally: copy admin/.env.example to admin/.env.local and fill it in.\n' +
      'On Netlify: Site settings -> Environment variables.'
  );
}

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true, // survive a refresh and a closed tab
    autoRefreshToken: true, // the SDK owns token refresh; we never do it by hand
    detectSessionInUrl: true, // REQUIRED: invite and recovery links arrive with tokens in the URL
    // Admin-generated invite/recovery links (auth.admin.generateLink) redirect
    // back with the session in the URL hash fragment — the "implicit" style.
    // A 'pkce' client looks for a ?code= query param and a locally-stored code
    // verifier that these links never produce, so it silently ignores the
    // fragment, establishes no session, and the recovery page reports "this
    // link didn't work". This app has no OAuth/social login (the only reason to
    // prefer PKCE), so implicit is both correct and simpler here.
    flowType: 'implicit',
  },
});

export default supabase;
